export const APP_VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
export const APP_VERSION_FETCH_TIMEOUT_MS = 8 * 1000;

const APP_VERSION_RELOAD_ATTEMPT_STORAGE_KEY =
  'sminy:app-version-reload-attempted';
const MAX_STORED_RELOAD_ATTEMPTS = 20;
const APP_VERSION_CHECK_QUERY_PARAM = '__sminy_version_check';
const NEW_VERSION_RELOAD_ATTEMPT_PREFIX = 'new-version:';
const CHUNK_RELOAD_ATTEMPT_PREFIX = 'chunk:';

const MAIN_JAVASCRIPT_PATH = /\/static\/js\/main(?:\.[a-z0-9_-]+)+\.js$/i;
const MAIN_STYLESHEET_PATH = /\/static\/css\/main(?:\.[a-z0-9_-]+)+\.css$/i;

export type ReloadAttemptStorage = Pick<Storage, 'getItem' | 'setItem'>;

export interface AppAssetVersion {
  id: string;
  scripts: string[];
  styles: string[];
}

export interface AppVersionCheckResult {
  loaded: AppAssetVersion | null;
  latest: AppAssetVersion | null;
  updateAvailable: boolean;
}

export interface CheckAppVersionOptions {
  documentRef?: Document;
  fetchImpl?: typeof fetch;
  now?: () => number;
  baseUrl?: string;
  timeoutMs?: number;
}

const normalizeAssetUrl = (
  value: string | null,
  baseUrl: string,
): { normalized: string; pathname: string } | null => {
  if (!value) return null;

  try {
    const url = new URL(value, baseUrl);
    return {
      normalized: `${url.origin}${url.pathname}`,
      pathname: url.pathname,
    };
  } catch {
    return null;
  }
};

const uniqueSorted = (values: string[]): string[] => (
  Array.from(new Set(values)).sort()
);

const buildAssetVersion = (
  documentRef: Document,
  baseUrl: string,
): AppAssetVersion | null => {
  const scripts = uniqueSorted(
    Array.from(documentRef.querySelectorAll<HTMLScriptElement>('script[src]'))
      .map((element) => normalizeAssetUrl(element.getAttribute('src'), baseUrl))
      .filter((asset): asset is NonNullable<typeof asset> => (
        Boolean(asset && MAIN_JAVASCRIPT_PATH.test(asset.pathname))
      ))
      .map((asset) => asset.normalized),
  );

  const styles = uniqueSorted(
    Array.from(
      documentRef.querySelectorAll<HTMLLinkElement>('link[rel~="stylesheet"][href]'),
    )
      .map((element) => normalizeAssetUrl(element.getAttribute('href'), baseUrl))
      .filter((asset): asset is NonNullable<typeof asset> => (
        Boolean(asset && MAIN_STYLESHEET_PATH.test(asset.pathname))
      ))
      .map((asset) => asset.normalized),
  );

  if (scripts.length === 0) {
    return null;
  }

  return {
    scripts,
    styles,
    id: [...scripts, ...styles].join('|'),
  };
};

export const readLoadedAppVersion = (
  documentRef: Document = document,
  baseUrl: string = documentRef.baseURI,
): AppAssetVersion | null => buildAssetVersion(documentRef, baseUrl);

export const readAppVersionFromHtml = (
  html: string,
  baseUrl: string,
): AppAssetVersion | null => {
  const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
  return buildAssetVersion(parsedDocument, baseUrl);
};

export const areAppVersionsEqual = (
  first: AppAssetVersion,
  second: AppAssetVersion,
): boolean => first.id === second.id;

export const checkAppVersion = async ({
  documentRef = document,
  fetchImpl = window.fetch.bind(window),
  now = Date.now,
  baseUrl = documentRef.baseURI,
  timeoutMs = APP_VERSION_FETCH_TIMEOUT_MS,
}: CheckAppVersionOptions = {}): Promise<AppVersionCheckResult> => {
  const loaded = readLoadedAppVersion(documentRef, baseUrl);
  const indexUrl = new URL('/index.html', baseUrl);
  indexUrl.searchParams.set(APP_VERSION_CHECK_QUERY_PARAM, String(now()));
  const abortController = new AbortController();
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error('Tempo limite ao verificar a versão do frontend.'));
      abortController.abort();
    }, Math.max(1, timeoutMs));
  });
  const requestPromise = (async (): Promise<AppVersionCheckResult> => {
    const response = await fetchImpl(indexUrl.toString(), {
      cache: 'no-store',
      credentials: 'same-origin',
      headers: {
        Accept: 'text/html',
      },
      signal: abortController.signal,
    });

    if (!response.ok) {
      throw new Error(`Falha ao verificar a versão do frontend (${response.status}).`);
    }

    const latest = readAppVersionFromHtml(await response.text(), indexUrl.toString());
    if (!latest) {
      throw new Error('O index atual não contém o bundle principal do frontend.');
    }

    return {
      loaded,
      latest,
      updateAvailable: Boolean(loaded && !areAppVersionsEqual(loaded, latest)),
    };
  })();

  try {
    return await Promise.race([requestPromise, timeoutPromise]);
  } finally {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
    }
  }
};

const getDefaultReloadAttemptStorage = (): ReloadAttemptStorage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

interface ReloadAttemptValuesResult {
  success: boolean;
  values: unknown[];
}

const readReloadAttemptValues = (
  storage: ReloadAttemptStorage,
): ReloadAttemptValuesResult => {
  try {
    const storedValue = storage.getItem(APP_VERSION_RELOAD_ATTEMPT_STORAGE_KEY);
    if (storedValue === null) return { success: true, values: [] };

    const parsedValue: unknown = JSON.parse(storedValue);
    return Array.isArray(parsedValue)
      ? { success: true, values: parsedValue }
      : { success: false, values: [] };
  } catch {
    return { success: false, values: [] };
  }
};

export const getVersionTransitionReloadKey = (
  loadedId: string,
  latestId: string,
): string => (
  `${NEW_VERSION_RELOAD_ATTEMPT_PREFIX}${JSON.stringify([loadedId, latestId])}`
);

const readVersionTransitionReloadKey = (
  reloadAttemptKey: string,
): readonly [string, string] | null => {
  if (!reloadAttemptKey.startsWith(NEW_VERSION_RELOAD_ATTEMPT_PREFIX)) {
    return null;
  }

  try {
    const parsedValue: unknown = JSON.parse(
      reloadAttemptKey.slice(NEW_VERSION_RELOAD_ATTEMPT_PREFIX.length),
    );
    if (
      !Array.isArray(parsedValue)
      || parsedValue.length !== 2
      || parsedValue.some((item) => typeof item !== 'string')
    ) {
      return null;
    }

    return [parsedValue[0], parsedValue[1]];
  } catch {
    return null;
  }
};

const isManagedReloadAttemptKey = (reloadAttemptKey: string): boolean => (
  reloadAttemptKey.startsWith(CHUNK_RELOAD_ATTEMPT_PREFIX)
  || readVersionTransitionReloadKey(reloadAttemptKey) !== null
);

const limitManagedReloadAttempts = (values: unknown[]): unknown[] => {
  const managedAttempts = values.filter((value): value is string => (
    typeof value === 'string' && isManagedReloadAttemptKey(value)
  ));
  let managedAttemptsToDrop = Math.max(
    0,
    managedAttempts.length - MAX_STORED_RELOAD_ATTEMPTS,
  );

  return values.filter((value) => {
    if (
      managedAttemptsToDrop > 0
      && typeof value === 'string'
      && isManagedReloadAttemptKey(value)
    ) {
      managedAttemptsToDrop -= 1;
      return false;
    }

    return true;
  });
};

export const wasReloadAttemptedForVersion = (
  version: string,
  storage: ReloadAttemptStorage | null = getDefaultReloadAttemptStorage(),
): boolean => {
  if (!storage) return false;

  const readResult = readReloadAttemptValues(storage);
  return readResult.success && readResult.values.includes(version);
};

export const markReloadAttemptedForVersion = (
  version: string,
  storage: ReloadAttemptStorage | null = getDefaultReloadAttemptStorage(),
): boolean => {
  if (!storage) return false;

  try {
    const readResult = readReloadAttemptValues(storage);
    if (!readResult.success) return false;
    if (readResult.values.includes(version)) return true;

    storage.setItem(
      APP_VERSION_RELOAD_ATTEMPT_STORAGE_KEY,
      JSON.stringify(limitManagedReloadAttempts([
        ...readResult.values,
        version,
      ])),
    );
    const verificationResult = readReloadAttemptValues(storage);
    return verificationResult.success
      && verificationResult.values.includes(version);
  } catch {
    return false;
  }
};

export const forgetReloadAttemptForVersion = (
  version: string,
  storage: ReloadAttemptStorage | null = getDefaultReloadAttemptStorage(),
): boolean => {
  if (!storage) return false;

  try {
    const readResult = readReloadAttemptValues(storage);
    if (!readResult.success) return false;

    const remainingAttempts = readResult.values
      .filter((attemptedVersion) => attemptedVersion !== version);
    if (remainingAttempts.length === readResult.values.length) return true;

    storage.setItem(
      APP_VERSION_RELOAD_ATTEMPT_STORAGE_KEY,
      JSON.stringify(remainingAttempts),
    );
    const verificationResult = readReloadAttemptValues(storage);
    return verificationResult.success
      && !verificationResult.values.includes(version);
  } catch {
    return false;
  }
};

export const forgetReloadAttemptsCompletedByVersion = (
  loadedVersionId: string,
  storage: ReloadAttemptStorage | null = getDefaultReloadAttemptStorage(),
): boolean => {
  if (!storage) return false;

  try {
    const readResult = readReloadAttemptValues(storage);
    if (!readResult.success) return false;

    const remainingAttempts = readResult.values.filter((reloadAttemptValue) => {
      if (typeof reloadAttemptValue !== 'string') return true;

      const reloadAttemptKey = reloadAttemptValue;
      const transition = readVersionTransitionReloadKey(reloadAttemptKey);
      return !transition || transition[1] !== loadedVersionId;
    });

    if (remainingAttempts.length === readResult.values.length) return true;

    storage.setItem(
      APP_VERSION_RELOAD_ATTEMPT_STORAGE_KEY,
      JSON.stringify(remainingAttempts),
    );
    const verificationResult = readReloadAttemptValues(storage);
    if (!verificationResult.success) return false;

    return verificationResult.values.every((reloadAttemptValue) => {
      if (typeof reloadAttemptValue !== 'string') return true;

      const reloadAttemptKey = reloadAttemptValue;
      const transition = readVersionTransitionReloadKey(reloadAttemptKey);
      return !transition || transition[1] !== loadedVersionId;
    });
  } catch {
    return false;
  }
};

const collectErrorText = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (!value || typeof value !== 'object') return '';

  const candidate = value as {
    message?: unknown;
    name?: unknown;
    reason?: unknown;
    target?: { href?: unknown; src?: unknown; tagName?: unknown } | null;
  };
  const fields = [candidate.name, candidate.message]
    .filter((field): field is string => typeof field === 'string');

  if (candidate.reason && candidate.reason !== value) {
    fields.push(collectErrorText(candidate.reason));
  }

  if (
    candidate.target?.tagName === 'SCRIPT' &&
    typeof candidate.target.src === 'string' &&
    /\.chunk\.js(?:[?#]|$)/i.test(candidate.target.src)
  ) {
    fields.push('ChunkLoadError');
  }

  if (
    candidate.target?.tagName === 'LINK' &&
    typeof candidate.target.href === 'string' &&
    /\.chunk\.css(?:[?#]|$)/i.test(candidate.target.href)
  ) {
    fields.push('ChunkLoadError');
  }

  return fields.join(' ');
};

export const isChunkLoadError = (value: unknown): boolean => {
  const errorText = collectErrorText(value);
  return (
    /ChunkLoadError/i.test(errorText) ||
    /Loading (?:CSS )?chunk [^ ]+ failed/i.test(errorText) ||
    /Failed to fetch dynamically imported module/i.test(errorText) ||
    /Importing a module script failed/i.test(errorText) ||
    /error loading dynamically imported module/i.test(errorText)
  );
};

export const getChunkFailureVersion = (
  loaded: AppAssetVersion | null = readLoadedAppVersion(),
): string => `${CHUNK_RELOAD_ATTEMPT_PREFIX}${loaded?.id || 'unknown'}`;
